import LegalPage from "./LegalPage";

export default function PrivacyPage() {
  return (
    <LegalPage
      eyebrow="Legal · The Philosopher Test"
      title="Privacy Policy"
      updated="July 20, 2026"
      testid="privacy-screen"
      path="/privacy"
      metaDescription="Privacy Policy for The Philosopher Test — what data we collect, why, how we store it, and your rights under GDPR and CCPA."
    >
      <p>
        This policy explains what we collect, why, how we store it, and the rights you have —
        including under the GDPR (EU/UK) and the CCPA (California).
      </p>

      <h2>1. Data we collect</h2>
      <ul>
        <li>
          <strong>Quiz answers &amp; computed traits</strong> — the option you pick on each of the
          20 questions and the six trait scores derived from them. These are stored under a
          random result ID and are not linked to your identity unless you provide an email
          address.
        </li>
        <li>
          <strong>Email address</strong> — only if you (a) submit the &ldquo;send my result to my
          email&rdquo; form on the result page, or (b) buy the Full Report (in which case Stripe
          collects it during checkout).
        </li>
        <li>
          <strong>Payment data</strong> — processed <em>entirely by Stripe</em>. We never see or
          store your card number. We only receive the payment status, the amount, and the
          associated email address.
        </li>
        <li>
          <strong>Basic technical data</strong> — such as your browser type and a timestamp, kept
          in short-lived logs for security and abuse prevention.
        </li>
      </ul>

      <h2>2. Why we use this data</h2>
      <ul>
        <li>To compute your philosopher match and generate your report.</li>
        <li>To send you your result and/or your Full Report by email (delivered via Resend).</li>
        <li>To operate, secure, and improve the Service (aggregate analytics only).</li>
      </ul>
      <p>We do not sell your data, and we do not use it for advertising profiling.</p>

      <h2>3. Sub-processors</h2>
      <ul>
        <li>
          <strong>Stripe, Inc.</strong> — payment processing. Their privacy policy:{" "}
          <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
            stripe.com/privacy
          </a>
          .
        </li>
        <li>
          <strong>Resend Inc.</strong> — transactional email delivery. Their privacy policy:{" "}
          <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">
            resend.com/legal/privacy-policy
          </a>
          .
        </li>
        <li>
          <strong>Anthropic PBC</strong> — AI report generation via the Claude API. Prompts and
          your trait scores are sent to Anthropic in order to produce your report; no email or
          payment data is sent. Their privacy policy:{" "}
          <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer">
            anthropic.com/legal/privacy
          </a>
          .
        </li>
      </ul>

      <h2>4. Retention</h2>
      <p>
        Anonymous quiz results and generated reports are retained for as long as the Service
        operates so that you can reopen your report link at any time. If you email us to request
        deletion, we will remove your data (and any associated email) within 30 days.
      </p>

      <h2>5. Your rights</h2>
      <p>
        Depending on your jurisdiction, you have the right to (a) access the data we hold about
        you, (b) correct it, (c) have it deleted, (d) restrict or object to processing, (e)
        receive a portable copy of it, and (f) lodge a complaint with your data-protection
        authority. To exercise any of these rights, email{" "}
        <a href="mailto:contact@thephilosophertest.com">contact@thephilosophertest.com</a>.
      </p>

      <h2>6. Cookies &amp; analytics</h2>
      <p>
        The Service does not set marketing or tracking cookies. We use{" "}
        <a href="https://plausible.io/privacy-focused-web-analytics" target="_blank" rel="noopener noreferrer">
          Plausible Analytics
        </a>{" "}
        to measure aggregate traffic. Plausible is a privacy-friendly, cookie-less
        analytics tool: it does not use cookies, does not collect any personal data, does
        not track visitors across sites or devices, and is fully GDPR, CCPA and PECR
        compliant. Stripe may set functional cookies on its own hosted checkout page for
        fraud prevention.
      </p>

      <h2>7. Changes</h2>
      <p>
        We will update this policy if our practices change. Material changes will be indicated by
        a new &ldquo;Last updated&rdquo; date.
      </p>

      <h2>8. Contact</h2>
      <p>
        Data controller: Mendola Jonathan (The Philosopher Test). Email:{" "}
        <a href="mailto:contact@thephilosophertest.com">contact@thephilosophertest.com</a>.
      </p>
    </LegalPage>
  );
}
